/**
 * Dashboard Service
 * Handles dashboard analytics and KPI data
 */

import api from './api';

class DashboardService {
  /**
   * Get admin dashboard KPIs
   * @param {string} companyId - Optional company filter
   */
  async getAdminKPIs(companyId = null) {
    const params = companyId ? `?company=${companyId}` : '';
    const response = await api.get(`/dashboard/admin/kpis${params}`);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error('Failed to fetch KPIs');
  }

  /**
   * Get throughput chart data
   * @param {string} companyId - Optional company filter
   */
  async getThroughput(companyId = null) {
    const params = companyId ? `?company=${companyId}` : '';
    const response = await api.get(`/dashboard/admin/throughput${params}`);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error('Failed to fetch throughput data');
  }

  /**
   * Get recent jobs for dashboard
   * @param {number} limit - Number of jobs to fetch
   * @param {string} companyId - Optional company filter
   */
  async getRecentJobs(limit = 10, companyId = null) {
    const params = new URLSearchParams();
    params.append('limit', limit);
    if (companyId) params.append('company', companyId);

    const response = await api.get(`/dashboard/admin/recent-jobs?${params.toString()}`);

    if (response.success && response.data) {
      return response.data.jobs || response.data;
    }

    throw new Error('Failed to fetch recent jobs');
  }

  /**
   * Get driver status list for dashboard
   * @param {number} limit - Number of drivers to fetch
   * @param {string} companyId - Optional company filter
   */
  async getDriversStatus(limit = 10, companyId = null) {
    const params = new URLSearchParams();
    params.append('limit', limit);
    if (companyId) params.append('company', companyId);

    const response = await api.get(`/dashboard/admin/drivers-status?${params.toString()}`);

    if (response.success && response.data) {
      return response.data.drivers || response.data;
    }

    throw new Error('Failed to fetch drivers status');
  }

  /**
   * Get general dashboard stats (requires moderator+)
   */
  async getStats() {
    const response = await api.get('/dashboard/stats');

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error('Failed to fetch stats');
  }

  /**
   * Get recent activity logs
   */
  async getActivity() {
    const response = await api.get('/dashboard/activity');

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error('Failed to fetch activity');
  }

  /**
   * Get system metrics
   */
  async getMetrics() {
    const response = await api.get('/dashboard/metrics');

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error('Failed to fetch metrics');
  }

  /**
   * Get comprehensive dashboard statistics
   * @param {string} companyId - Optional company filter
   */
  async getComprehensiveStats(companyId = null) {
    const params = companyId ? `?company=${companyId}` : '';
    const response = await api.get(`/dashboard/admin/comprehensive${params}`);

    if (response.success && response.data) {
      return response.data.stats || response.data;
    }

    throw new Error('Failed to fetch comprehensive stats');
  }

  /**
   * Get companies for filter dropdown
   */
  async getCompaniesForFilter() {
    const response = await api.get('/dashboard/companies');

    if (response.success && response.data) {
      return response.data.companies || [];
    }

    throw new Error('Failed to fetch companies');
  }

  /**
   * Get export data
   * @param {Object} filters - Export filters
   */
  async getExportData(filters = {}) {
    const params = new URLSearchParams();
    if (filters.company) params.append('company', filters.company);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);

    const queryString = params.toString();
    const url = `/dashboard/admin/export${queryString ? `?${queryString}` : ''}`;

    const response = await api.get(url);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error('Failed to fetch export data');
  }
}

const dashboardService = new DashboardService();
export default dashboardService;
